"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import {
  HiPhotograph,
  HiDownload,
  HiUpload,
  HiRefresh,
  HiQuestionMarkCircle,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import JSZip from "jszip";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { chromeExtensionPromoTileGeneratorFAQ } from "@/lib/data/faq/chrome-extension-promo-tile-generator";

interface TileSpec {
  label: string;
  width: number;
  height: number;
  filename: string;
}

const TILE_SPECS: TileSpec[] = [
  { label: "Small Tile", width: 440, height: 280, filename: "promo-small-440x280.png" },
  { label: "Large Tile", width: 920, height: 680, filename: "promo-large-920x680.png" },
  { label: "Marquee", width: 1400, height: 560, filename: "promo-marquee-1400x560.png" },
];

interface GeneratedTile extends TileSpec {
  dataUrl: string;
}

export default function PromoTileGeneratorClient() {
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [generatedTiles, setGeneratedTiles] = useState<GeneratedTile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file (PNG, JPG).",
        variant: "destructive",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const src = event.target?.result as string;
      setSourceImage(src);
      processImage(src);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const processImage = async (src: string) => {
    setIsProcessing(true);
    setGeneratedTiles([]);

    const img = new Image();
    img.src = src;
    await new Promise((resolve) => {
      img.onload = resolve;
    });

    const tiles: GeneratedTile[] = [];

    for (const spec of TILE_SPECS) {
      const canvas = document.createElement("canvas");
      canvas.width = spec.width;
      canvas.height = spec.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;

      // High-quality resize
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Fill with white background (in case source has transparency)
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, spec.width, spec.height);

      // Cover-fit: scale source to fully cover target, crop to center
      const sourceRatio = img.width / img.height;
      const targetRatio = spec.width / spec.height;
      let sx = 0,
        sy = 0,
        sw = img.width,
        sh = img.height;

      if (sourceRatio > targetRatio) {
        // Source is wider — crop horizontally
        sw = img.height * targetRatio;
        sx = (img.width - sw) / 2;
      } else {
        // Source is taller — crop vertically
        sh = img.width / targetRatio;
        sy = (img.height - sh) / 2;
      }

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, spec.width, spec.height);

      tiles.push({
        ...spec,
        dataUrl: canvas.toDataURL("image/png"),
      });
    }

    setGeneratedTiles(tiles);
    setIsProcessing(false);
  };

  const downloadZip = async () => {
    if (generatedTiles.length === 0) return;
    const zip = new JSZip();
    generatedTiles.forEach((tile) => {
      const base64Data = tile.dataUrl.replace(/^data:image\/png;base64,/, "");
      zip.file(tile.filename, base64Data, { base64: true });
    });
    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const a = document.createElement("a");
    a.href = url;
    a.download = "chrome-web-store-promo-tiles.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: "Download started",
      description: "Your promo tiles have been bundled into a ZIP file.",
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Chrome Extension Promo Tile Generator"
        description="Generate all 3 Chrome Web Store promo tile sizes from a single image."
        url="https://plugmonkey.xyz/tool/chrome-extension-promo-tile-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Promo Tile Generator",
            url: "https://plugmonkey.xyz/tool/chrome-extension-promo-tile-generator/",
          },
        ]}
        faq={chromeExtensionPromoTileGeneratorFAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-purple-600/10 rounded-full text-purple-600">
          <HiPhotograph className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Chrome Extension Promo Tile Generator</h1>
          <p className="text-muted-foreground mt-1">
            Small (440x280), Large (920x680), and Marquee (1400x560) — generated from one image.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Publishing a Chrome extension? The Chrome Web Store requires up to 3 promotional tile
          images at specific dimensions (Small 440 x 280, Large 920 x 680, Marquee 1400 x 560).
          Extensions WITH all 3 get more visibility in featured carousels and search results;
          extensions with only the required small tile get less promotion.
        </p>
        <p>
          This tool takes a single high-resolution source image (1400 wide or larger recommended)
          and generates all 3 tiles instantly with proper aspect-ratio handling — center-cropping
          where needed to match each target ratio. Everything processes in your browser; no upload,
          no signup, no watermark.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiUpload /> Upload Source Image
              </CardTitle>
              <CardDescription>
                Upload at least 1400 x 560 pixels for best results. Wider source = better quality
                across all 3 tile sizes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
                  isDragging ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                aria-label="Upload image file to generate promo tiles"
              >
                <input
                  id="promo-tile-image"
                  name="image"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png, image/jpeg"
                  className="hidden"
                  aria-label="Image file input"
                />
                {sourceImage ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={sourceImage}
                      alt="Uploaded source image for promo tile generation"
                      className="max-w-full max-h-48 object-contain rounded-lg shadow-sm mb-4"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <HiRefresh className="mr-2" /> Change Image
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-muted-foreground">
                    <HiUpload className="w-12 h-12 mb-4 opacity-50" />
                    <p className="font-medium">Click to upload or drag and drop</p>
                    <p className="text-sm opacity-70 mt-1">PNG or JPG supported</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {generatedTiles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Generated Tiles</CardTitle>
                <CardDescription>
                  Preview of all 3 Chrome Web Store promo tile sizes.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {generatedTiles.map((tile) => (
                    <div key={tile.label} className="flex flex-col gap-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{tile.label}</span>
                        <span className="text-xs font-mono text-muted-foreground">
                          {tile.width} × {tile.height}
                        </span>
                      </div>
                      <div className="bg-muted/20 rounded-lg p-4 overflow-x-auto">
                        <img
                          src={tile.dataUrl}
                          alt={`${tile.label} (${tile.width}x${tile.height})`}
                          className="rounded shadow-sm max-w-full h-auto"
                          style={{ maxHeight: "200px" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-muted/50 border-primary/20 shadow-lg">
              <CardHeader>
                <CardTitle>Download</CardTitle>
                <CardDescription>All 3 tiles in one ZIP, ready for the Web Store.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={downloadZip}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12"
                  disabled={generatedTiles.length === 0 || isProcessing}
                  suppressHydrationWarning
                >
                  <HiDownload className="mr-2 h-5 w-5" />
                  {isProcessing ? "Processing..." : "Download ZIP Bundle"}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Processed entirely in your browser. No files are uploaded.
                </p>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Need icons too?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Generate Chrome Extension icons (16, 32, 48, 128) from a single image with the
                companion tool.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/tool/chrome-extension-icon-resizer/">Go to Icon Resizer</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={chromeExtensionPromoTileGeneratorFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
